// Single source of truth for "what kind of viewer is this?"
//
// Reads the cached user record (kept fresh by /auth/me) and returns the
// canonical role + boolean flags every component should branch on.
//
// Roles (mirror of backend role_helpers.derive_role):
//   • anon              — no token at all
//   • fan               — logged in, not paid, not super_admin
//   • new_creator       — paid platform fee, but built zero silos yet
//   • creator           — paid platform fee, built ≥ 1 silo (or is_paid_creator==true)
//   • affiliate_creator — is_affiliate=true ($300 gate paid)
//   • super_admin       — Randy / users.role==super_admin
//
// `isOperator` = anyone with Creator Dashboard access (super_admin || paid).
// Fans never see operator chrome.

import { getToken, getUser } from "@/lib/api";
import { hasPlatformAccess } from "@/lib/access";

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  affiliate_creator: "Creator Pro",
  creator: "Creator",
  new_creator: "New Creator",
  fan: "Fan",
  anon: "Visitor",
};

// Frontend mirror of role_helpers.derive_role(). Keep these two in sync
// or things will drift. The backend is authoritative; this is for cases
// where we have a cached user object and don't want to await the
// /me/identity round-trip.
export function deriveRole(user, siloCount = 0) {
  if (!user) return "anon";
  if (user.role === "super_admin") return "super_admin";
  if (user.is_affiliate) return "affiliate_creator";
  const isPaid = hasPlatformAccess(user);
  if (isPaid) return (siloCount || 0) >= 1 ? "creator" : "new_creator";
  return "fan";
}

export function useViewerSurface() {
  const token = getToken();
  const user = getUser();

  if (!token || !user) {
    return {
      role: "anon",
      isAnon: true,
      isFan: false,
      isCreator: false,
      isNewCreator: false,
      isAffiliate: false,
      isSuper: false,
      isOperator: false,
      hasDashboardAccess: false,
      user: null,
    };
  }

  // Prefer the backend-computed derived_role if the cached user has it;
  // fall back to local derivation for any legacy cached payload.
  const derived =
    user.derived_role ||
    deriveRole(user, user.silo_count || 0);

  const isSuper = derived === "super_admin";
  // Super admins inherently count as affiliates (founder = first member, top tier).
  const isAffiliate = derived === "affiliate_creator" || isSuper;
  const isCreator = derived === "creator";
  const isNewCreator = derived === "new_creator";
  const isFan = derived === "fan";
  const isOperator = isSuper || isAffiliate || isCreator || isNewCreator;

  return {
    role: derived,
    isAnon: false,
    isFan,
    isCreator,
    isNewCreator,
    isAffiliate,
    isSuper,
    isOperator,
    hasDashboardAccess: isOperator,
    user,
  };
}
